"use client";
import { contactInfo, footerLinks } from "@/data/main-data";
import { useStore } from "@/state-management/context";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import CopyRights from "./CopyRights";

export default function Footer() {
  const locale = useLocale();
  const t = useTranslations("Nav");
  const pathname = usePathname();
  const router = useRouter();
  const { setTermsTab } = useStore();

  if (pathname.includes("auth")) return null;

  return (
    <footer className="w-full relative overflow-hidden bg-slate-950">
      <div className="absolute inset-0 z-0">
        <Image
          src="/shutterstock_2092214953 1.png"
          alt="Footer Background"
          fill
          className="object-cover opacity-30"
          priority
        />
        <div className="absolute inset-0 bg-linear-to-b from-slate-950/90 via-main/80 to-slate-950"></div>
      </div>

      <div className="sm:container px-6 md:px-0 lg:w-[85%] mx-auto relative z-10 pt-20 pb-10 text-white">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="flex flex-col gap-6 col-span-1 lg:col-span-1">
            <div className="relative w-40 h-15">
              <Image
                src="/logo.png"
                alt="SNA Alaqaria Logo"
                fill
                className="object-contain"
              />
            </div>
            <p className="text-slate-300 leading-relaxed text-sm sm:text-base max-w-sm">
              {locale === "en"
                ? "Experience the future of real estate in Saudi Arabia. We provide premium property solutions with transparency and excellence."
                : "اختبر مستقبل العقارات في المملكة العربية السعودية. نقدم حلولاً عقارية فاخرة تتركز على الشفافية والتميز في الخدمة."}
            </p>
          </div>

          <div className="flex flex-col">
            <h3 className="text-xl font-bold mb-6 relative w-fit">
              {t("quickLinks")}
              <span className="absolute -bottom-2 left-0 w-1/2 h-1 bg-emerald-500 rounded-full"></span>
            </h3>
            <ul className="flex flex-col gap-4">
              {footerLinks.map((link) => (
                <li key={link.label}>
                  {"tab" in link ? (
                    <button
                      onClick={() => {
                        setTermsTab(link.tab);
                        router.push(link.href);
                      }}
                      className="text-slate-300 cursor-pointer hover:text-emerald-400 hover:translate-x-2 transition-all duration-300 flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-100 transition-all"></span>
                      {t(link.label)}
                    </button>
                  ) : (
                    <Link
                      href={link.href}
                      className="text-slate-300 hover:text-emerald-400 hover:translate-x-2 transition-all duration-300 flex items-center gap-2 group"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-100 transition-all"></span>
                      {t(link.label)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col lg:col-span-2">
            <h3 className="text-xl font-bold mb-6 relative w-fit">
              {t("contactInformation")}
              <span className="absolute -bottom-2 left-0 w-1/2 h-1 bg-emerald-500 rounded-full"></span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {contactInfo.map((e) => (
                <div
                  key={e.id}
                  className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-emerald-500/50 transition-all duration-300 group"
                >
                  <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white transition-all">
                    <e.icon size={20} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span
                      className="text-sm font-bold text-white tabular-nums"
                      style={{ unicodeBidi: "plaintext" }}
                    >
                      {locale === "ar" && e.valueAr ? e.valueAr : e.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full h-px bg-linear-to-r from-transparent via-white/10 to-transparent mt-16"></div>

        <CopyRights />
      </div>
    </footer>
  );
}
